import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n';

interface Subscription {
  platformOwner?: boolean;
  status: string;
  planName: string;
  daysLeft: number | null;
  messagesSent: number;
  messagesLimit: number;
  messagesLeft: number;
  dailyLimit?: number;
  messagesSentToday?: number;
  messagesLeftToday?: number | null;
  canSend: boolean;
  blockMessage: string | null;
}

// Mostra o aviso só quando importa: bloqueado, vencendo ou perto do limite
function tone(sub: Subscription): 'blocked' | 'warning' | null {
  if (!sub.canSend) return 'blocked';
  if (sub.daysLeft !== null && sub.daysLeft <= 3) return 'warning';
  if (sub.messagesLimit > 0 && sub.messagesLeft / sub.messagesLimit <= 0.1) return 'warning';
  // Teto do dia quase no fim: quem está mandando campanha precisa saber antes de parar
  if ((sub.dailyLimit || 0) > 0 && (sub.messagesLeftToday ?? 0) / (sub.dailyLimit || 1) <= 0.1) return 'warning';
  // Plano sem limite mensal não mostra contagem
  return null;
}

// Nome do plano traduzido a partir do identificador devolvido pela API
const PLAN_KEYS: Record<string, string> = {
  'Free trial': 'sub.planTrial',
  Starter: 'sub.planStarter',
  Pro: 'sub.planPro',
  Business: 'sub.planBusiness',
  'Internal (no limits)': 'sub.planInternal',
};

export function SubscriptionBanner() {
  const t = useT();
  const { token } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    if (!token) return;
    let active = true;

    const load = () => {
      fetch('/api/subscription', { headers: { Authorization: `Bearer ${token}` } })
        .then(response => (response.ok ? response.json() : null))
        .then(data => { if (active && data && !data.platformOwner) setSubscription(data); })
        .catch(() => undefined);
    };

    load();
    const timer = setInterval(load, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [token]);

  if (!subscription) return null;
  const planKey = PLAN_KEYS[subscription.planName];
  const planName = planKey ? t(planKey) : subscription.planName;
  const level = tone(subscription);
  if (!level) return null;

  const styles = level === 'blocked'
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-amber-50 border-amber-200 text-amber-900';

  return (
    <div className={`border-b px-4 py-2 text-sm ${styles}`}>
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-4 gap-y-1 justify-between">
        <span>
          {level === 'blocked'
            ? subscription.blockMessage
            : subscription.daysLeft !== null && subscription.daysLeft <= 3
              ? t('sub.ends', { plan: planName, days: Math.max(subscription.daysLeft, 0) })
              : (subscription.dailyLimit || 0) > 0 && (subscription.messagesLeftToday ?? 0) / (subscription.dailyLimit || 1) <= 0.1
                ? t('sub.leftToday', { left: subscription.messagesLeftToday ?? 0 })
                : t('sub.left', { left: subscription.messagesLeft, plan: planName })}
        </span>
        <span className="text-xs opacity-80 flex gap-3">
          {subscription.messagesLimit > 0 && (
            <span>
              {t('sub.usage', {
                used: subscription.messagesSent.toLocaleString('en-US'),
                limit: subscription.messagesLimit.toLocaleString('en-US'),
              })}
            </span>
          )}
          {(subscription.dailyLimit || 0) > 0 && (
            <span>
              {t('sub.usageToday', {
                used: (subscription.messagesSentToday || 0).toLocaleString('en-US'),
                limit: (subscription.dailyLimit || 0).toLocaleString('en-US'),
              })}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
